import { Q } from '@/lib/route';
import { ParsedQs } from 'qs';

export interface FilterState {
    category?: string[];
    vendor?: string[];
    style?: string[];
    packagingType?: string[];
    volume?: string[];
    minPrice?: number;
    maxPrice?: number;
    minAbv?: number;
    maxAbv?: number;
    minIbu?: number;
    maxIbu?: number;
}

export interface FilterQp {
    category?: string[] | string;
    vendor?: string[] | string;
    style?: string[] | string;
    packagingType?: string[] | string;
    volume?: string[] | string;
    minPrice?: number | string;
    maxPrice?: number | string;
    minAbv?: number | string;
    maxAbv?: number | string;
    minIbu?: number | string;
    maxIbu?: number | string;
}

function isNumberField(
    key: keyof FilterQp,
): key is 'minPrice' | 'maxPrice' | 'minAbv' | 'maxAbv' | 'minIbu' | 'maxIbu' {
    return [
        'minPrice',
        'maxPrice',
        'minAbv',
        'maxAbv',
        'minIbu',
        'maxIbu',
    ].includes(key as string);
}

export function getQueryString(v: Q, fallback = ''): string | ParsedQs {
    if (typeof v === 'string') return v;

    if (Array.isArray(v)) return typeof v[0] === 'string' ? v[0] : fallback;

    if (typeof v === 'object' && v !== null) {
        return v;
    }
    return fallback;
}

export function stringToArray(value: string | undefined): string[] {
    if (!value) return [];
    return value.split(',').filter((item) => item.trim() !== '');
}

export function isValidInteger(str: string): boolean {
    const num = Number(str);
    return Number.isInteger(num) && String(num) === str;
}

export function isArrayField(
    key: keyof FilterQp,
): key is 'category' | 'vendor' | 'style' | 'packagingType' | 'volume' {
    return ['category', 'vendor', 'style', 'volume', 'packagingType'].includes(
        key as string,
    );
}

export function getInitialFilters(filtersQp: Q): FilterState {
    const normalizedFilters: FilterState = {};

    if (!filtersQp || typeof filtersQp !== 'object') {
        return normalizedFilters;
    }

    const parsedFilters = filtersQp as ParsedQs;

    (Object.keys(parsedFilters) as Array<keyof FilterQp>).forEach((k) => {
        const value = parsedFilters[k];

        if (value !== undefined) {
            if (isNumberField(k)) {
                if (typeof value === 'string' && isValidInteger(value)) {
                    normalizedFilters[k] = Number(value);
                }
            } else if (isArrayField(k)) {
                if (typeof value === 'string') {
                    normalizedFilters[k] = stringToArray(value);
                } else if (
                    Array.isArray(value) &&
                    value.every(
                        (item): item is string => typeof item === 'string',
                    )
                ) {
                    normalizedFilters[k] = value as string[];
                }
            }
        }
    });

    return normalizedFilters;
}
